import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import type { ReactNode } from "react";
import { DOCUMENT_SECTIONS, type DocumentProvider, type EvidenceProvider, type PackageProvider } from "../../types/evidence";
import { packageFileOf } from "./packageFiles";
import { SECTION_ROWS } from "./sectionRows";

export type HelpTab = "how" | "setup" | "split";

const STEPS = [
  ["Set the case details", "Customer contact, plus the ARN for card disputes."],
  ["Generate", "The portal pulls the order, tracking, IP location and policies, plus the product page for a Shopify store, then writes the conclusion."],
  ["Fix what it flags", "Attach support screenshots and delivery photos, then Update evidence."],
  ["Download gives the whole document and one file per field; upload each into its field.", "A bank case gets one PDF, every section on its own page."],
];

const SETUP = [
  ["Policies", "Pasted, or seeded from a Shopify store, on the company page › Evidence."],
  ["Logo and checkout screenshot", "Uploaded once per storefront."],
  ["Renewal order tags", "Only if the subscription app tags renewals with its own words."],
  ["Shopify stores: customer-data access", "Without it the customer name and phone print blank."],
];

const LEFT_OUT: { title: string; text: string; cardOnly?: true }[] = [
  { title: "IP Location", text: "printed when the IP resolves to the billing city, ZIP or state; a same-state match carries a note." },
  { title: "Delivery", text: "claimed only when the carrier shows delivery to the right area." },
  { title: "Secured Payment", text: "only when a card check passed.", cardOnly: true },
  { title: "Order History", text: "renewals only." },
];

// In print order.
const partsFor = (provider: PackageProvider) => [
  ...DOCUMENT_SECTIONS.map((section) => ({
    part: SECTION_ROWS[section].heading,
    where: packageFileOf(provider, section).label,
  })),
  { part: "Conclusion", where: "Goes with the Facts" },
];

const BY_REASON = [
  ["Not received", "tracking and proof of delivery first."],
  ["Unauthorized", "order details, proof of delivery, checkout and terms."],
  ["Not as described", "product page, customer communication, refund policy."],
  ["Cancelled subscription", "order history, cancellation policy, customer communication."],
];

const PROCESSOR: Record<EvidenceProvider, string> = { paypal: "PayPal", shopify: "Shopify", stripe: "Stripe", adyen: "Adyen" };

function Pairs({ items, numbered }: { items: string[][]; numbered?: boolean }) {
  return (
    <Box component={numbered ? "ol" : "ul"} sx={{ pl: numbered ? 2.5 : 2, m: 0 }}>
      {items.map(([title, text]) => (
        <Typography key={title} component="li" variant="body2" sx={{ mb: 1 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>{title}</Box>{numbered ? ". " : ": "}{text}
        </Typography>
      ))}
    </Box>
  );
}

function Panel({ id, current, children }: { id: HelpTab; current: HelpTab; children: ReactNode }) {
  return (
    <Box role="tabpanel" id={`help-panel-${id}`} aria-labelledby={`help-tab-${id}`} hidden={id !== current} sx={{ pt: 2 }}>
      {id === current && children}
    </Box>
  );
}

export function HelpMark({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <IconButton aria-label={label} onClick={onClick} size="small" sx={{ p: 0.25 }}>
      <HelpOutlineIcon sx={{ fontSize: 16 }} />
    </IconButton>
  );
}

export default function EvidenceHelpDialog({ tab, provider, onTabChange, onClose }: {
  /** Null keeps the dialog closed. */
  tab: HelpTab | null;
  /** Null for a processor the generator does not serve: the split tab is then left out. */
  provider: DocumentProvider | null;
  onTabChange: (tab: HelpTab) => void;
  onClose: () => void;
}) {
  const current = tab ?? "how";
  const leftOut = LEFT_OUT.filter((item) => !(item.cardOnly && provider === "paypal")).map(({ title, text }) => [title, text]);

  return (
    <Dialog open={tab !== null} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="help-title">
      <DialogTitle id="help-title" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 0 }}>
        Evidence help
        <IconButton aria-label="Close help" onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Tabs value={current} onChange={(_, value) => onTabChange(value as HelpTab)} variant="fullWidth" aria-label="Help topics">
          <Tab label="How it works" value="how" id="help-tab-how" aria-controls="help-panel-how" />
          <Tab label="Set up a storefront" value="setup" id="help-tab-setup" aria-controls="help-panel-setup" />
          {provider && <Tab label="Split the PDF" value="split" id="help-tab-split" aria-controls="help-panel-split" />}
        </Tabs>

        <Panel id="how" current={current}>
          <Pairs items={STEPS} numbered />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Anything the data cannot prove is left out, not guessed.</Typography>
          {provider === "shopify" && (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              Save puts the split files into their categories and the written answers from the column on the right. Shopify keeps the response editable and sends it to the bank on the due date; to send it earlier, open the dispute in Shopify, pick the reason and press Submit now. Saving again replaces the portal's earlier files. A store that has not granted the dispute evidence scopes can only download; reinstalling the Chargemont app on the store grants them.
            </Typography>
          )}
          {provider === "paypal" && (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              Submit sends PayPal one response: every split file as its documents, the tracking as PayPal's own fields when PayPal asked for proof of fulfillment and the document proved a parcel, and the note from the column on the right, prefilled with the conclusion. PayPal takes the response at once and moves the case to under review. When PayPal reopens the case and asks again, Submit comes back here for another round. A PayPal app without the Disputes update permission can only download.
            </Typography>
          )}
          {provider === "adyen" && (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              Submit uploads the whole document under every document type the chosen reason requires, or under the one it fits when none is required, and then defends; the split files are for Customer Area uploads only; Adyen sends it to the issuer at once and takes nothing more. Only the reasons Adyen allows for the chargeback are offered; one the document cannot fill is greyed out with what it needs, and that one goes through Customer Area. A credential without the API dispute management role can only download.
            </Typography>
          )}
          {provider === "stripe" && (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              Submit uploads each split file under its Stripe evidence field and the other sections as the uncategorized file, so every page goes once, sends the answers as text and submits the dispute; Stripe passes it to the issuer at once and takes nothing more. Text answers count toward Stripe's 50 pages. A restricted key without Disputes write and Files write can only download.
            </Typography>
          )}
        </Panel>

        <Panel id="setup" current={current}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>Once per storefront, under Company → Evidence. A storefront is one selling domain; a Shopify store is one automatically, a CheckoutChamp domain appears the first time a dispute names it. A missing item shows under Findings on every dispute for that storefront.</Typography>
          <Pairs items={SETUP} />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Why a section is left out</Typography>
          <Pairs items={leftOut} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>A left-out IP Location or Order History can be ticked back in on the Sections card; it then prints without a match claim.</Typography>
        </Panel>

        {provider && (
          <Panel id="split" current={current}>
            {provider === "bank" ? (
              <Typography variant="body2">
                Upload the PDF in the bank&apos;s portal. Every section starts on a new page, so split it by page range if the portal wants several files.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Download gives the whole document and one file per field; upload each into its field.
                </Typography>
                <Table size="small" aria-label="Where each part of the PDF goes">
                  <TableHead>
                    <TableRow>
                      <TableCell>Part, in page order</TableCell>
                      <TableCell>Upload in {PROCESSOR[provider]}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partsFor(provider).map((row) => (
                      <TableRow key={row.part}>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>{row.part}</TableCell>
                        <TableCell>{row.where}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {provider !== "adyen" && (
                  <Typography variant="body2" sx={{ mt: 1.5 }}>
                    Facts of the Case and the Conclusion also go into the processor&apos;s written field: Shopify Additional details, PayPal Your written response, Stripe Uncategorized text.
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  A file that lists several sections is their page ranges merged into one PDF, in document order.
                </Typography>
              </>
            )}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>What to send first, by reason</Typography>
            <Pairs items={BY_REASON} />
          </Panel>
        )}
      </DialogContent>
    </Dialog>
  );
}
